export interface Service {
  title: string;
  description: string;
  icon: string;
}

export const services = [
  {
    title: 'Defect Identification & Documentation',
    description: 'Capture construction defects with photos, annotations, priority levels, and location context for clear accountability.',
    icon: 'ID',
  },
  {
    title: 'Snagging Workflows & Reporting',
    description: 'Standardize defect reporting, assignment, resolution tracking, and exportable handover documentation.',
    icon: 'QA',
  },
  {
    title: 'Visual Defect Mapping',
    description: 'Pin issues on plans, photos, and project zones so every team understands exactly where work is required.',
    icon: 'MP',
  },
  {
    title: 'Mobile Snagging App',
    description: 'Give site teams a responsive field experience for fast inspections, updates, and evidence capture.',
    icon: 'MB',
  },
  {
    title: 'Team Collaboration & Notifications',
    description: 'Coordinate owners, contractors, due dates, approvals, and notifications from one shared workspace.',
    icon: 'TM',
  },
  {
    title: 'AI-Powered QA & Insights',
    description: 'Use AI-assisted review to surface quality risks, spot patterns, and focus teams on the right actions.',
    icon: 'AI',
  },
];

export interface ProductSection {
  title: string;
  bullets: string[];
}

export const productSections: ProductSection[] = [
  {
    title: "Safety & Compliance Snagging",
    bullets: [
      "Identify safety hazards and compliance issues on-site",
      "Real-time defect tracking with priority levels",
      "Audit trails for regulatory compliance",
      "Mobile access for inspectors and contractors"
    ],
  },
  {
    title: "Quality Control Snagging",
    bullets: [
      "Document workmanship defects and material issues",
      "Version-controlled defect logs with resolution status",
      "Automated notifications for defect assignments",
      "Integration with quality management systems"
    ],
  },
  {
    title: "Construction Progress Snagging",
    bullets: [
      "Track defects across construction phases",
      "Sync data across teams and subcontractors",
      "Smart forms with conditional logic for defect types",
      "Real-time dashboards for project oversight"
    ],
  },
  {
    title: "Defect & Snagging Management",
    bullets: [
      "Pin defects on plans, photos, and 3D models",
      "Multiple views: list, map, timeline, and 3D",
      "Fast defect assignment and resolution workflows",
      "Offline support for remote construction sites"
    ],
  },
];

export interface Feature {
  title: string;
  desc: string;
}

export const features: Feature[] = [
  { title: "Defect Checklists & Inspections", desc: "Customizable snagging checklists, automated defect tracking, milestone-based inspections, and exportable reports" },
  { title: "Smart Snagging Forms", desc: "No-code form builder for defect types, conditional logic, photo annotations, and automated routing" },
  { title: "Progress Tracking & Timelines", desc: "Digital defect logs, resolution timelines, subcontractor tracking, and progress analytics" },
  { title: "Asset & Site Management", desc: "Centralized defect database, site mapping, proactive maintenance scheduling, and mobile work orders" },
];

// ===== COMPANY INFORMATION =====
export interface CompanyInfo {
  name: string;
  tagline: string;
  description: string;
  website: string;
  email: string;
}

export const companyInfo: CompanyInfo = {
  name: "Arqite",
  tagline: "Snagging Made Simple",
  description: "We're revolutionizing construction snagging by providing intelligent digital tools that streamline defect identification, documentation, and resolution for superior project quality.",
  website: "www.arqite.com",
  email: "hello@arqite.com",
};

// ===== ABOUT US =====
export interface AboutSection {
  title: string;
  subtitle: string;
  content: string;
  values: string[];
  mission: string;
  vision: string;
}

export const aboutData: AboutSection = {
  title: "About Arqite",
  subtitle: "Transforming Construction Snagging",
  content: "Founded by construction professionals who witnessed the inefficiencies of traditional paper-based snagging, Arqite was created to digitize and streamline the defect management process.",
  values: [
    "Innovation in Defect Tracking",
    "Customer-Centric Solutions",
    "Data-Driven Quality Control",
    "Transparent Defect Resolution",
    "Continuous Process Improvement"
  ],
  mission: "To empower construction teams with cutting-edge snagging tools that turn defect identification into actionable insights for flawless project delivery.",
  vision: "A construction industry where snagging is proactive, efficient, and contributes to building excellence through digital innovation."
};



// ===== TESTIMONIALS =====
export interface Testimonial {
  quote: string;
  author: string;
  position: string;
  company: string;
  rating: number;
}


// ===== CONTACT INFORMATION =====
export interface ContactInfo {
  email: string;
  supportEmail: string;
  salesEmail: string;
  hours: string;
  social: {
    linkedin: string;
    twitter: string;
    youtube: string;
  };
}

export const contactInfo: ContactInfo = {
  email: "hello@arqite.com",
  supportEmail: "support@arqite.com",
  salesEmail: "sales@arqite.com",
  hours: "Monday - Friday: 9:00 AM - 5:00 PM (GMT)",
  social: {
    linkedin: "https://linkedin.com/company/arqite",
    twitter: "https://twitter.com/arqite",
    youtube: "https://youtube.com/@arqite"
  }
};
